import { Calendar as CalendarIcon } from 'lucide-react';
import { FilterSummaryChip } from '@/components/knowledge-graph/FilterSummaryChip';
import { formatRegionalDate, type RegionalDateLocale } from '@/utils/date-utils';

export interface ChronologyFilterSummaryProps {
  eventsCount: number;
  filteredEventsCount: number;
  hasActiveFilters: boolean;
  activeSources: ReadonlySet<string>;
  activeEntities: ReadonlySet<string>;
  activeCreators: ReadonlySet<string>;
  dateFrom: string;
  dateTo: string;
  locale: RegionalDateLocale;
  onToggleSource: (value: string) => void;
  onToggleEntity: (value: string) => void;
  onToggleCreator: (value: string) => void;
  onClearDateRange: () => void;
  onClearAllFilters: () => void;
}

export function ChronologyFilterSummary({
  eventsCount,
  filteredEventsCount,
  hasActiveFilters,
  activeSources,
  activeEntities,
  activeCreators,
  dateFrom,
  dateTo,
  locale,
  onToggleSource,
  onToggleEntity,
  onToggleCreator,
  onClearDateRange,
  onClearAllFilters,
}: ChronologyFilterSummaryProps) {
  const summaryLabel = filteredEventsCount === eventsCount
    ? `Showing all ${eventsCount} ${eventsCount === 1 ? 'event' : 'events'}`
    : `Showing ${filteredEventsCount} of ${eventsCount} ${eventsCount === 1 ? 'event' : 'events'}`;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[11px] text-gray-500 shrink-0">
        {summaryLabel}
      </span>
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClearAllFilters}
          className="text-[11px] font-medium text-gray-500 hover:text-gray-700 shrink-0"
        >
          Clear all filters
        </button>
      )}
      {Array.from(activeSources).map(name => (
        <FilterSummaryChip key={name} label="Source" onRemove={() => onToggleSource(name)}>
          {name}
        </FilterSummaryChip>
      ))}
      {Array.from(activeEntities).map(name => (
        <FilterSummaryChip key={name} label="Entity" onRemove={() => onToggleEntity(name)}>
          {name}
        </FilterSummaryChip>
      ))}
      {Array.from(activeCreators).map(name => (
        <FilterSummaryChip key={name} label="Created by" onRemove={() => onToggleCreator(name)}>
          {name}
        </FilterSummaryChip>
      ))}
      {(dateFrom || dateTo) && (
        <FilterSummaryChip icon={<CalendarIcon className="h-2.5 w-2.5 text-gray-300" />} onRemove={onClearDateRange}>
          {dateFrom && dateTo
            ? `${formatRegionalDate(dateFrom, locale)} - ${formatRegionalDate(dateTo, locale)}`
            : dateFrom ? `From ${formatRegionalDate(dateFrom, locale)}` : `To ${formatRegionalDate(dateTo, locale)}`}
        </FilterSummaryChip>
      )}
    </div>
  );
}
